import React, { useCallback, useContext, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from 'react-router-dom';
import AppButton from '@/components/buttons/AppButton';
import FormField from '@/components/forms/FormField';
import CenterConatainer from "@/components/layouts/CenterContainer";
import LogoHeader from "@/components/layouts/LogoHeader";
import { useAuth } from "@/context/providers/AuthProvider";
import { OrganizationContext } from "@/context/providers/OrganizationProvider";
import { login, loginWithAzure, msal } from '@/services/auth.service';
import { BUTTONS, HEADINGS, LABELS, PLACEHOLDERS } from "@/utils/constants/appConstants/commonConstants";
import { SIGN_IN_WITH_ENTRA_ID } from "@/utils/constants/appConstants/DirectoryConstants";
import { ERROR_CODES, PORTAL_TYPE } from '@/utils/constants/constants';
import { INVALID_EMAIL_ADDRESS, INVALID_CREDENTIALS_LOGIN_MESSAGE, REPEATED_INVALID_LOGIN_ATTEMPTS_MESSAGE } from '@/utils/constants/messages';
import { checkSSOLoginAllowed, checkSSOLoginRestricted, getFromLocalStorage, isEmail, setDataInLocalStorage, storeLoginPasswordForOtp, storeLoginUserData } from '@/utils/functions/helperFunctions';
import { infoToasterMessage } from '@/utils/functions/toaster';
import { getemail_address } from '@/utils/functions/TokenFunction';

type Props = {};

function AdLoginScreen({ }: Props) {
    const [password, setPassword] = useState("");
    const [msg, setMsg] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [loaderLoginWithSSO, setLoaderLoginWithSSO] = useState(false);
    const [queryParams, setQueryParams] = useSearchParams();
    const [organizationId, setOrganizationId] = useState<any>(getFromLocalStorage('switch_org_id'));
    const email = getemail_address();
    const isDirectoryServiceEnabled = checkSSOLoginAllowed();
    const isLoginRestrictedViaSSO = checkSSOLoginRestricted();
    const navigate = useNavigate();
    const { setToken } = useAuth();
    const [loginWithSSO, setLoginWithSSO] = useState(false);
    const { setOrganization } = useContext(OrganizationContext);

    useEffect(() => {
        const orgFromParams = queryParams.get('organization');
        if (orgFromParams) { setOrganizationId(orgFromParams); setDataInLocalStorage('switch_org_id', orgFromParams); }
    }, []);

    useEffect(() => { const loginWithSSOString = getFromLocalStorage('loginWithSSO'); setLoginWithSSO(loginWithSSOString === 'true'); }, []);

    useEffect(() => {
        if (queryParams.has('code')) {
            setLoaderLoginWithSSO(true);
            const data = { code: queryParams.get('code'), organization: organizationId, email };
            loginWithAzure(data, (res: any) => {
                queryParams.delete('code');
                if (queryParams.has('client_info')) queryParams.delete('client_info');
                if (queryParams.has('session_state')) queryParams.delete('session_state');
                setQueryParams(queryParams);
                storeLoginUserData(res.data);
                setOrganization(res.data.current_organization);
                setToken(res.data.access_token);
                localStorage.removeItem('switch_org_id');
                localStorage.setItem('loginWithSSO', 'true');
                setLoaderLoginWithSSO(false);
                navigate('/');
            }, () => setLoaderLoginWithSSO(false));
        }
    }, []);

    const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => { setMsg(""); setPassword(e.target.value); };

    const handleSubmit = useCallback((e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setIsLoading(true);
        if (!isEmail(email)) { setIsLoading(false); setMsg(INVALID_EMAIL_ADDRESS); return; }
        login(JSON.stringify({ email_address: email, password, portal_type: "admin", organization: organizationId }),
            (res: any) => {
                setIsLoading(false);
                storeLoginPasswordForOtp(password);
                navigate(`/verify-otp?organization=${organizationId}`);
            },
            (err: any) => {
                setIsLoading(false);
                if (err?.response?.status === 403 && err?.response?.data?.error_code === ERROR_CODES.IP_WHITELISTING_ERROR_CODE) infoToasterMessage(err?.response?.data?.message);
                else if (err?.response?.data?.statusCode === 401) setMsg(INVALID_CREDENTIALS_LOGIN_MESSAGE);
                else if (err?.response?.data?.error_code === 527 || err.response?.data?.error_code === 528) setMsg(REPEATED_INVALID_LOGIN_ATTEMPTS_MESSAGE);
                else setMsg(err?.response?.data?.message);
            }
        );
    }, [email, password, setIsLoading]);

    const PasswordForm = () => (
        <form method="POST" id="signInForm" onSubmit={handleSubmit} autoComplete="off">
            <div className="mb-4">
                <label className="text-sm font-medium text-foreground mb-1 block" htmlFor="login-form-password-input" id="login-form-password-label">{LABELS.PASSWORD}</label>
                <FormField value={password} onChange={handlePasswordChange} type="password" id="login-form-password-input" placeholder={PLACEHOLDERS.PASSWORD} required />
            </div>
            {msg && <p className="text-destructive text-sm mb-3">{msg}</p>}
            <div>
                <AppButton id="sign-in-btn" isLoading={isLoading} btnText={BUTTONS.SIGN_IN} className="btn-lg btn-block btn-primary" type="submit" />
            </div>
        </form>
    );

    const SSOButton = () => (
        <div className='mt-2'>
            <AppButton icon={<em className='icon ni ni-microsoft mr-2' />} id='sso-login-btn' isLoading={loaderLoginWithSSO} btnText={SIGN_IN_WITH_ENTRA_ID} className={`btn btn-lg ${!isLoginRestrictedViaSSO ? 'btn-outline-primary' : 'btn-primary'} btn-block`} onClick={async () => msal(organizationId, email, PORTAL_TYPE.ADMIN, (url: string) => window.location.assign(url))} />
        </div>
    );

    return (
        <CenterConatainer>
            <LogoHeader id="login-form" heading={HEADINGS.LOGIN} children={undefined} />
            {!isDirectoryServiceEnabled && loginWithSSO ? <PasswordForm /> : <PasswordForm />}
            {isDirectoryServiceEnabled ? (!isLoginRestrictedViaSSO ? (<><PasswordForm /><SSOButton /></>) : <SSOButton />) : null}
        </CenterConatainer>
    );
}

export default AdLoginScreen;
